import React, { useState } from "react"
import { Form, FastField, withFormik } from "formik"
import * as Yup from "yup"
import { parsePhoneNumber } from "libphonenumber-js"
import { ROUTES } from "../../../common/utils"
import { API_PUBLIC } from "../../../api"
import DialogBox from "../../../common/DialogBox"
import Terms from "../../../landing/Terms"
import Privacy from "../../../landing/PrivacyPolicy"
import { TextField, MobileNumberField, CheckBox } from "../../../common/Fields"
import "./GuestForm.css"

function BaseGuestForm({
  touched,
  errors,
  values,
  backHandler,
  isSubmitting,
  checkoutMethod,
  customerData
}) {
  const [showTerms, setShowTerms] = useState(false)
  function toggleShowModal(e) {
    e.preventDefault()
    setShowTerms(!showTerms)
  }
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  function toggleShowPrivacyModal(e) {
    e.preventDefault()
    setShowPrivacyModal(!showPrivacyModal)
  }
  return (
    <Form className="GuestForm">
      <h1 className="header">Customer Information </h1>
      <div className="name">
        <span>
          <label>First Name</label>
          <FastField component={TextField} name="firstName" type="text" />
        </span>
        <span>
          <label>Last Name</label>
          <FastField component={TextField} name="lastName" type="text" />
        </span>
      </div>
      <span>
        <label>Email Address</label>
        <FastField component={TextField} name="email" type="text" />
      </span>
      <span>
        <label>Mobile Number</label>
        <MobileNumberField
          groupName="mobile"
          countryCodeName="countryCode"
          mobileNumberName="mobileNumber"
          errors={errors}
          touched={touched}
          values={values}
          disabled={
            customerData &&
            customerData.mobile &&
            customerData.mobile["mobileNumber"] &&
            customerData.mobile["countryCode"]
          }
        />
      </span>
      <span className="checkbox-container">
        <FastField
          component={CheckBox}
          name="terms"
          checked={values.terms}
          placeholder={[
            "I accept the",
            <a
              key="link"
              href={ROUTES.TERMS}
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleShowModal}
            >
              Terms and Conditions
            </a>,
            " and ",
            <a
              key="privacy-link"
              href={ROUTES.TERMS}
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleShowPrivacyModal}
            >
              Privacy Policy
            </a>
          ]}
        />
      </span>
      <button type="submit" className="black" disabled={isSubmitting}>
        {(checkoutMethod && "Proceed to shipping method") ||
          (isSubmitting && "Checking out as guest") ||
          "Checkout as guest"}
      </button>
      <button
        type="button"
        className="gray"
        onClick={backHandler}
        disabled={isSubmitting}
      >
        Back
      </button>
      {showTerms && (
        <DialogBox
          className="terms-modal"
          primaryAction={null}
          primaryActionLabel={""}
          secondaryAction={null}
          secondaryActionLabel=""
          close={toggleShowModal}
          closeLabel=""
          title=""
        >
          <Terms />
        </DialogBox>
      )}
      {showPrivacyModal && (
        <DialogBox
          primaryAction={null}
          primaryActionLabel={""}
          secondaryAction={null}
          secondaryActionLabel=""
          close={toggleShowPrivacyModal}
          closeLabel=""
          title=""
        >
          <Privacy />
        </DialogBox>
      )}
    </Form>
  )
}

const FormikGuestForm = withFormik({
  mapPropsToValues: ({ customerData, deliveryData }) => ({
    account: (customerData && customerData.account) || "",
    firstName:
      deliveryData &&
      deliveryData.courier_id &&
      deliveryData.method === "lalamove" &&
      deliveryData.misc &&
      deliveryData.misc.delivery &&
      deliveryData.misc.delivery.firstname !== ""
        ? deliveryData.misc.delivery.firstname || ""
        : (customerData && customerData.firstName) || "",
    lastName:
      deliveryData &&
      deliveryData.courier_id &&
      deliveryData.method === "lalamove" &&
      deliveryData.misc &&
      deliveryData.misc.delivery &&
      deliveryData.misc.delivery.lastname !== ""
        ? deliveryData.misc.delivery.lastname || ""
        : (customerData && customerData.lastName) || "",
    mobile:
      deliveryData &&
      deliveryData.courier_id &&
      deliveryData.method === "lalamove" &&
      deliveryData.misc &&
      deliveryData.misc.deliveryMobile &&
      deliveryData.misc.deliveryMobile.countryCode !== "" &&
      deliveryData.misc.deliveryMobile.mobileNumber !== ""
        ? deliveryData.misc.deliveryMobile || {
            countryCode: "",
            mobileNumber: ""
          }
        : (customerData && {
            countryCode:
              (customerData.mobile && customerData.mobile.countryCode) || "",
            mobileNumber:
              (customerData.mobile && customerData.mobile.mobileNumber) || ""
          }) || {
            countryCode: "",
            mobileNumber: ""
          },
    email: (customerData && customerData.email) || "",
    terms: (customerData && customerData.terms) || false
  }),
  validationSchema: Yup.object().shape({
    firstName: Yup.string().required("Please enter first name."),
    lastName: Yup.string().required("Please enter last name."),
    mobile: Yup.object()
      .shape({
        countryCode: Yup.string()
          .matches(
            /^($)|(\+?\d{1,3}$|\d{1,4}$)/,
            "Please enter a valid country code."
          )
          .max(4, "Please enter a valid country code.")
          .typeError("Country code and mobile number should be filled.")
          .required("Please enter a country code."),
        mobileNumber: Yup.string()
          .matches(/^($)|([0-9]{4,13}$)/, "Please enter a valid mobile number.")
          .max(13, "Please enter a valid mobile number.")
          .required("Please enter a mobile number.")
          .typeError("Country code and mobile number should be filled.")
      })
      .test("test-phone", "Mobile number is invalid.", function(mobile) {
        if (`${mobile.countryCode}${mobile.mobileNumber}` !== "") {
          try {
            parsePhoneNumber(`${mobile.countryCode}${mobile.mobileNumber}`)
            return true
          } catch (e) {
            return false
          }
        }
        return true
      }),
    email: Yup.string()
      .email("Please enter a valid email.")
      .required("Please enter an email."),
    terms: Yup.boolean().oneOf(
      [true],
      "Please indicate that you accepted the Terms & Conditions and Privacy Policy."
    )
  }),
  handleSubmit: async (
    values,
    { resetForm, props, setSubmitting, setErrors, setFieldError }
  ) => {
    if (props.checkoutMethod) {
      props.setActiveStep("shipping")
    } else {
      try {
        const response = await API_PUBLIC.get(
          `/customers/${values.account}/${values.mobile.countryCode}${
            values.mobile.mobileNumber
          }`
        )
        const data = response.data
        if (data && !data.is_valid_mobile) {
          throw new Error("customers/invalid-mobile-number")
        }
        // const mobileAlreadyUsed =
        //   data && data.customer && data.customer.id
        //     ? data.customer.id !== props.customerData.id
        //     : false
        // if (mobileAlreadyUsed) {
        //   throw new Error("customers/mobile-already-exists")
        // }
        const adr = data.address
        //Set Customer Info
        props.setCustomerStatus(!data.has_account, data.customer.id)
        if (data && data.address && Object.keys(data.address).length > 0) {
          props.setCustomerAddress({
            addressOne: adr.address_line_1 || "",
            addressTwo: adr.address_line_2 || "",
            city:
              adr.country === "philippines" && adr.city
                ? { value: adr.city, label: adr.city }
                : adr.city || "",
            barangay:
              (adr.country === "philippines" &&
                adr.barangay && { value: adr.barangay, label: adr.barangay }) ||
              "",
            country:
              (adr.country && { value: adr.country, label: adr.country }) || "",
            state:
              (adr.province && { value: adr.province, label: adr.province }) ||
              "",
            zipcode: adr.zipcode || ""
          })
        }
        //customer has existing account and is using a mobile number that doesn't exist in database
        if (data.has_account) {
          props.setExistingCustomer("account")
          props.setFormValues(values)
          props.setCustomerData({
            firstName: values.firstName,
            lastName: values.lastName,
            mobile: values.mobile,
            email: values.email,
            terms: values.terms,
            method: "guest",
            id: (data && data.customer && data.customer.id) || null
          })
        } else {
          //customer has no existing account, is not a new guest, and is using a mobile number that doesn't exist in database
          if (!data.is_guest) {
            props.setExistingCustomer("guest")
            props.setCustomerData({
              firstName: values.firstName,
              lastName: values.lastName,
              mobile: values.mobile,
              email: values.email,
              terms: values.terms,
              method: "guest",
              id: (data && data.customer && data.customer.id) || null
            })
            props.setFormValues(values)
          } else {
            //customer has no account and is guest
            props.setCustomerData({
              firstName: values.firstName,
              lastName: values.lastName,
              mobile: values.mobile,
              email: values.email,
              terms: values.terms,
              method: "guest",
              id: (data && data.customer && data.customer.id) || null
            })
            props.setActiveStep("shipping")
            props.setCheckoutMethod("guest")
          }
        }
      } catch (error) {
        console.log(error.response || error)
        if (error && error.message === "customers/invalid-mobile-number") {
          setFieldError("mobile", "Mobile number is invalid.")
        }
        if (error && error.message === "customers/mobile-already-exists") {
          setFieldError("mobile", "Mobile number is already in use.")
        }
      } finally {
        setSubmitting(false)
      }
    }
  }
})(BaseGuestForm)

class GuestForm extends React.Component {
  state = {
    existingCustomer: false,
    existingCustomerType: "guest",
    existingCustomerDialogShown: false,
    form: {}
  }

  showExistingDialog = () => {
    this.setState({
      existingCustomerDialogShown: true
    })
  }

  setExistingCustomer = type => {
    this.setState(
      state => ({
        ...state,
        existingCustomerType: type,
        existingCustomer: true
      }),
      () => {
        this.showExistingDialog()
      }
    )
  }

  hideExistingDialog = () => {
    this.setState({ existingCustomerDialogShown: false })
  }

  setFormValues = values => {
    this.setState({ form: values })
  }

  continueAsGuest = () => {
    const { setCustomerData, setActiveStep, setCheckoutMethod } = this.props
    const { form } = this.state
    setCustomerData({
      firstName: form.firstName,
      lastName: form.lastName,
      mobile: form.mobile,
      email: form.email,
      terms: form.terms,
      method: "guest"
    })
    setActiveStep("shipping")
    setCheckoutMethod("guest")
  }

  goToLogin = () => {
    const { emailLoginHandler } = this.props
    emailLoginHandler && emailLoginHandler()
  }

  goToRegister = () => {
    const { showAccountRegister } = this.props
    showAccountRegister()
  }

  closeDialogBox = () => {
    this.setState({ existingCustomerDialogShown: false })
  }

  render() {
    const { existingCustomerDialogShown, existingCustomerType } = this.state
    const { setCustomerAddress } = this.props
    return (
      <>
        <FormikGuestForm
          {...this.props}
          checkExistingCustomer={this.checkExistingCustomer}
          setExistingCustomer={this.setExistingCustomer}
          setCustomerAddress={setCustomerAddress}
          setFormValues={this.setFormValues}
        />
        {existingCustomerDialogShown && (
          <DialogBox
            primaryAction={this.continueAsGuest}
            primaryActionLabel={"Continue as guest"}
            secondaryAction={
              existingCustomerType === "account"
                ? this.goToLogin
                : this.goToRegister
            }
            secondaryActionLabel={
              existingCustomerType === "account"
                ? "Log-in to your account"
                : "Register for an account"
            }
            close={this.closeDialogBox}
            closeLabel="Cancel"
            title={
              existingCustomerType === "account"
                ? "Existing Account"
                : "Existing Mobile"
            }
          >
            <p>
              {existingCustomerType === "account"
                ? "The mobile number you entered is already registered with us. Kindly log-in your account."
                : "You're back! Looks like you've checkout with this mobile number before."}
            </p>
          </DialogBox>
        )}
      </>
    )
  }
}

export default GuestForm
